'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ISearchParams } from '@/ts/interfaces';
import { cn } from '@/helpers/common';
import NextButton from '@public/svgs/Next.svg';
import { PaginationComplexClassName } from '@/ts/types';

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  searchParams: ISearchParams;
  elementClassName?: PaginationComplexClassName;
  disableNext?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  searchParams,
  elementClassName,
  disableNext = false,
}) => {
  const [pages, setPages] = useState<number[]>([]);

  const buildPaginationLink = (page: number) => {
    const queryParams = new URLSearchParams(searchParams);
    queryParams.set('page', page.toString());
    return `?${queryParams.toString()}`;
  };

  useEffect(() => {
    const pagesArr = [];
    const maxPagesToShow = 5;

    pagesArr.push(1);

    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPagesToShow);
    }
    if (currentPage + 2 >= totalPages) {
      startPage = Math.max(2, totalPages - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      if (currentPage - startPage >= 2 && totalPages > 5 && i === startPage) {
        // pagesArr.push({ page: i, text: "...", link: `?page=${i}` });
      } else if (endPage - currentPage >= 2 && totalPages > 5 && i === endPage) {
        // pagesArr.push({ page: i, text: "...", link: `?page=${i}` });
      } else {
        pagesArr.push(i);
      }
    }
    if (totalPages > 1 && !pagesArr.find((p) => p === totalPages)) {
      pagesArr.push(totalPages);
    }

    setPages(pagesArr);
  }, [currentPage, totalPages]);

  return (
    <div className={'flex justify-center gap-3 pb-10 max-md:px-0 md:px-4'}>
      <Link
        href={buildPaginationLink((currentPage as number) - 1)}
        className={cn(
          'inline-flex justify-center items-center py-1 px-2 no-underline text-main-text-color text-sm leading-none bg-secondary default-transition hover:bg-primary hover:text-white hover:fill-white',
          elementClassName?.base,
          {
            'pointer-events-none cursor-default opacity-50':
              currentPage && (currentPage as number) <= 1,
          },
        )}
      >
        <NextButton className={cn('w-2 h-[14px] rotate-180 fill-main-text-color')} />
      </Link>
      {pages.map((p) => (
        <Link
          key={p}
          href={buildPaginationLink(p)}
          className={cn(
            'inline-flex justify-center items-center py-1 px-2 no-underline text-main-text-color text-sm leading-none bg-secondary default-transition hover:bg-primary hover:text-white hover:fill-white',
            elementClassName?.base,
            { 'bg-primary': currentPage == p },
            currentPage == p && cn(elementClassName?.selected),
          )}
        >
          {p}
        </Link>
      ))}

      <Link
        href={buildPaginationLink((currentPage as number) + 1)}
        className={cn(
          'inline-flex justify-center items-center py-1 px-2 no-underline text-main-text-color text-sm leading-none bg-secondary default-transition hover:bg-primary hover:text-white hover:fill-white',
          elementClassName?.base,
          {
            'pointer-events-none cursor-default opacity-50':
              (currentPage as number) >= totalPages || disableNext,
          },
        )}
      >
        <NextButton className={cn('w-2 h-[14px] fill-main-text-color')} />
      </Link>
    </div>
  );
};

export default Pagination;
